import React, { useState, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { cn } from '../../utils/helpers';
import { COLORS } from '../../utils/theme';

const TableView = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Column definitions
  const columns = [
    { key: 'surname', label: 'Surname' },
    { key: 'firstName', label: 'First Name' },
    { key: 'institution', label: 'Institution' },
    { key: 'project', label: 'Project' },
    { key: 'outputType', label: 'Output Type' },
    { key: 'year', label: 'Year' },
    { key: 'reference', label: 'Reference', isLink: true }
  ];

  // Sorting logic
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  // Pagination logic
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  // Sorting handler
  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: 
        current.key === key && current.direction === 'asc' 
          ? 'desc' 
          : 'asc',
    }));
  };

  // Render cell content
  const renderCell = (row, column) => {
    if (column.isLink && row[column.key]) {
      return (
        <a 
          href={row[column.key]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 hover:underline"
        >
          Link
        </a>
      );
    }
    return row[column.key];
  };

  return (
    <div className="flex flex-col">
      {/* Table Controls */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium">Rows per page:</label>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="rounded border p-1 text-sm"
            style={{ borderColor: COLORS.primary.main }}
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="text-sm" style={{ color: COLORS.primary.dark }}>
          Total entries: {data.length}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg" style={{ borderColor: COLORS.primary.main }}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr style={{ backgroundColor: COLORS.primary.dark }}>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                  onClick={() => handleSort(column.key)}
                  style={{ color: COLORS.neutral.white }}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {sortConfig.key === column.key && (
                      sortConfig.direction === 'asc' 
                        ? <ChevronUpIcon className="w-4 h-4" />
                        : <ChevronDownIcon className="w-4 h-4" />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.map((row, index) => (
              <tr 
                key={row.id} 
                className={cn(
                  "hover:bg-opacity-90 transition-colors",
                  index % 2 === 0 
                    ? 'bg-white' 
                    : 'bg-opacity-5'
                )}
                style={{ 
                  backgroundColor: index % 2 === 0 
                    ? COLORS.neutral.white 
                    : `${COLORS.primary.main}15` // 15 is the opacity in hex
                }}
              >
                {columns.map((column) => (
                  <td
                    key={`${row.id}-${column.key}`}
                    className="px-6 py-4 whitespace-nowrap text-sm"
                    style={{ color: COLORS.primary.dark }}
                  >
                    {renderCell(row, column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={cn(
              "px-3 py-1 rounded border transition-colors",
              currentPage === 1 
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-opacity-90"
            )}
            style={{
              backgroundColor: COLORS.primary.main,
              color: COLORS.neutral.white,
              borderColor: COLORS.primary.main
            }}
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={cn(
              "px-3 py-1 rounded border transition-colors",
              currentPage === totalPages 
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-opacity-90"
            )}
            style={{
              backgroundColor: COLORS.primary.dark,
              color: COLORS.neutral.white,
              borderColor: COLORS.primary.dark
            }}
          >
            Next
          </button>
        </div>
        <div className="text-sm" style={{ color: COLORS.primary.dark }}>
          Page {currentPage} of {totalPages}
        </div>
      </div>
    </div>
  );
};

export default TableView;